/**
 * Доставка ассетов клиенту: один способ для всех реализаций сцены.
 *
 * Всё лежит в одной директории — `src/public/` — и ходит по HTTP: SVG-листы из Figma (клетки и
 * аватарки) и подложка неба `bokeh-*.webp` (адрес даёт `space.ts`). В JS-бандл ничего не вшито, поэтому ни лобби,
 * ни одна реализация не тянет чужих байт, а браузер кеширует файлы сам. Лист читается один раз
 * (`loadSheet`) и дальше живёт строкой: `html` рисует символ картинкой, `pixi` растеризует
 * символ в текстуру. Отдельных копий под движок нет.
 */

import { useEffect, useState } from 'react'
import { AVATAR_SHEET } from './blocks/avatars.js'

/** Адрес `src/public/` на клиенте: ассеты лежат в нём прямо, без вложенных папок. */
export const ASSET_DIR = '/'

export const avatarSheetUrl = ASSET_DIR + AVATAR_SHEET

const sheets = new Map<string, Promise<string>>()

/** Текст SVG-листа. Один запрос на адрес, дальше — из памяти. */
export function loadSheet(url: string): Promise<string> {
  let sheet = sheets.get(url)
  if (!sheet) {
    sheet = fetch(url).then((response) => {
      if (!response.ok) throw new Error(`${url}: ${response.status}`)
      return response.text()
    })
    // Неудачу не кешируем: следующий вызов пробует снова.
    sheet.catch(() => sheets.delete(url))
    sheets.set(url, sheet)
  }
  return sheet
}

/** Листы для разметки: пока не пришли — пустая строка, потом перерисовка. */
export function useSheets(urls: readonly string[]): string {
  const [text, setText] = useState('')
  const key = urls.join()
  useEffect(() => {
    let live = true
    Promise.all(urls.map(loadSheet))
      .then((all) => live && setText(all.join('')))
      .catch((error) => console.warn(`[assets] ${error instanceof Error ? error.message : String(error)}`))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return text
}
